import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { 
  subscribeToAuthChanges, 
  login as authLogin, 
  logout as authLogout,
  persistSession,
  hasPersistedSession
} from '../services/authService';
import { PageLoader } from '../components/ui';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    // Restore persisted session flag if it exists
    if (hasPersistedSession()) {
      setLoading(true);
    }
    
    // Subscribe to auth state changes
    const unsubscribe = subscribeToAuthChanges(async (user) => {
      setUser(user);
      setLoading(false);
      persistSession(!!user);
    });
    
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);
  
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      await authLogin(email, password);
    } catch (err: any) {
      setError(err.message || 'Giriş yapılamadı');
      setLoading(false);
      throw err;
    }
  };
  
  const logout = async () => {
    try {
      setLoading(true);
      setError(null);
      await authLogout();
    } catch (err: any) {
      setError(err.message || 'Çıkış yapılamadı');
      setLoading(false);
      throw err;
    }
  };
  
  const clearError = () => {
    setError(null);
  };
  
  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, clearError }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected route wrapper component
interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  fallback = null 
}) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader fullScreen message="Yükleniyor..." />;
  }
  
  if (!user) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}; 